import React, { FC, ReactNode } from 'react';
import { ImplicitMixedControlProps } from '@cld/common-definitions';
import { Menu, MenuStateProps, useMenuState } from '@cld/menu';
import Popover, { PopoverProps } from '@cld/popover';
import { useExplicitSelectionState } from './DropdownMenu.hooks';
import { useDropdownMenu } from './headless/useDropdownMenu';
import { useDropdownMenuState } from './headless/useDropdownMenuState';

type SupportedPopoverProps = Pick<PopoverProps, 'placement' | 'afterVisibleChange' | 'align' | 'getTooltipContainer' | 'minWidth' | 'maxWidth'>;

interface DropdownMenuBaseProps extends SupportedPopoverProps {
  items: ReactNode;
  focusOnClose?:boolean;
}

export type DropdownMenuProps = DropdownMenuBaseProps & MenuStateProps & ImplicitMixedControlProps<'opened', boolean>;

export const DropdownMenu: FC<DropdownMenuProps> = ({
  defaultOpened,
  isOpened,
  onOpenedChange,
  children,
  items,
  onSelect,
  minWidth,
  maxWidth,
  defaultFocusedId,
  defaultSelectedId,
  focusedId,
  selectedId,
  onFocusedIdChange,
  focusOnClose=true,
  ...rest
}) => {
  const explicitSelectionState = useExplicitSelectionState({ defaultSelectedId, selectedId, onSelect });
  const menuState = useMenuState({
    defaultFocusedId,
    focusedId,
    onFocusedIdChange,
    ...explicitSelectionState,
  });
  const dropdownMenuState = useDropdownMenuState({ defaultOpened, isOpened, onOpenedChange }, menuState);
  const dropdownMenu = useDropdownMenu({ items, trigger: children, focusOnClose }, dropdownMenuState);

  const menu = (
    <Menu
      variant={{ selectionVariant: 'subtle', borderRadius: 'md' }}
      {...dropdownMenu.menuProps}
      ref={dropdownMenu.navigationRef}
    >
      {dropdownMenu.connectedItems}
    </Menu>
  );

  return (
    <Popover
      placement="bottomRight"
      animation="zoom"
      trigger="click"
      minWidth={minWidth}
      maxWidth={maxWidth}
      paperProps={{ isPadded: false, borderRadius: 'md' }}
      overlay={menu}
      {...dropdownMenu.popoverProps}
      {...rest}
    >
      {dropdownMenu.connectedTrigger}
    </Popover>
  );
};
